import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { InteractiveParticles } from "../components/InteractiveParticles";
import {
  Logo,
  Button,
  Input,
  Form,
  FormField,
  FormItem,
  FormControl,
  Alert,
  AlertTitle,
  AlertDescription,
} from "@pentatonic-ltd/ui";
import { CheckCircle } from "@solar-icons/react";

export const Route = createFileRoute("/reset-password")({
  validateSearch: (search: Record<string, unknown>) => ({
    token: (search.token as string) ?? "",
  }),
  component: ResetPasswordPage,
});

const ASSETS = {
  kiehls: "/images/kiehls.png",
  bq: "/images/bq.png",
  hyundai: "/images/hyundai.png",
  starbucks: "/images/starbucks.png",
};

function RightPanel() {
  return (
    <div className="hidden lg:flex flex-1 bg-[#171717] relative overflow-hidden">
      <InteractiveParticles className="absolute inset-0" />
      <div className="absolute bottom-12 left-0 right-0 flex flex-col gap-6 items-center pointer-events-none z-10">
        <p className="font-mono font-extralight text-[14px] uppercase tracking-[0.28px] text-white/60 text-center">
          Trusted by the world's leading consumer brands
        </p>
        <div className="flex gap-20 items-center">
          <div className="h-[37px] w-[74px] shrink-0">
            <img src={ASSETS.kiehls} alt="Kiehl's" className="h-full w-full object-contain object-left" />
          </div>
          <div className="h-[35px] w-[35px] shrink-0">
            <img src={ASSETS.bq} alt="B&Q" className="h-full w-full object-contain" />
          </div>
          <div className="h-[16px] w-[125px] shrink-0">
            <img src={ASSETS.hyundai} alt="Hyundai" className="h-full w-full object-contain" />
          </div>
          <div className="h-[37px] w-[37px] shrink-0">
            <img src={ASSETS.starbucks} alt="Starbucks" className="h-full w-full object-contain" />
          </div>
        </div>
      </div>
    </div>
  );
}

interface ResetPasswordFormValues {
  password: string;
  confirmPassword: string;
}

const ERROR_MESSAGES: Record<string, string> = {
  passwords_mismatch: "Passwords do not match.",
  password_too_short: "Password must be at least 8 characters.",
  token_expired: "This reset link has expired. Please request a new one.",
  token_invalid: "This reset link is invalid. Please request a new one.",
};

function ResetPasswordPage() {
  const { token } = Route.useSearch();
  const navigate = useNavigate();
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const form = useForm<ResetPasswordFormValues>({
    defaultValues: { password: "", confirmPassword: "" },
  });

  if (!token) {
    return (
      <div className="min-h-screen w-full flex bg-[#171717]">
        <div className="w-full lg:w-[560px] lg:shrink-0 bg-white dark:bg-[#171717] flex flex-col min-h-screen">
          <div className="flex flex-col flex-1 w-full max-w-[560px] mx-auto px-10 pt-12 pb-10 gap-10">
            <a href="/"><Logo variant="auto" size="lg" /></a>
            <Alert variant="error">
              <AlertTitle>Invalid link</AlertTitle>
              <AlertDescription>
                This reset link is missing a token.{" "}
                <a href="/forgot-password" className="underline">Request a new one →</a>
              </AlertDescription>
            </Alert>
          </div>
        </div>
        <RightPanel />
      </div>
    );
  }

  const onSubmit = async (values: ResetPasswordFormValues) => {
    setError(null);

    if (values.password !== values.confirmPassword) {
      setError("Passwords do not match.");
      form.setError("confirmPassword", { message: "Passwords do not match" });
      return;
    }

    try {
      const res = await fetch("/api/v1/auth/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token,
          password: values.password,
          confirmPassword: values.confirmPassword,
        }),
      });
      const result = await res.json() as { success: boolean; error?: string };

      if (!result.success) {
        const msg = ERROR_MESSAGES[result.error ?? ""] ?? "Something went wrong. Please try again.";
        setError(msg);
        if (result.error === "token_expired" || result.error === "token_invalid") {
          // Token is no good — don't mark fields red
        } else if (result.error === "passwords_mismatch") {
          form.setError("confirmPassword", { message: "Passwords do not match" });
        }
        return;
      }

      setSuccess(true);
      setTimeout(() => navigate({ to: "/" }), 3000);
    } catch {
      setError("Something went wrong. Please try again.");
    }
  };

  return (
    <div className="min-h-screen w-full flex bg-[#171717]">
      <div className="w-full lg:w-[560px] lg:shrink-0 bg-white dark:bg-[#171717] flex flex-col min-h-screen">
        <div className="flex flex-col flex-1 w-full max-w-[560px] mx-auto px-10 pt-12 pb-10">

          {!success ? (
            <div className="flex flex-col gap-10">
              <a href="/"><Logo variant="auto" size="lg" /></a>

              <div className="flex flex-col gap-10">
                <div className="flex flex-col gap-2">
                  <h1 className="text-[29px] leading-[1.4] font-light text-[#171717] dark:text-white">
                    Choose a new password
                  </h1>
                  <p className="text-[18px] leading-[1.4] font-light text-[#A9A9A9]">
                    Must be at least 8 characters.
                  </p>
                </div>

                <Form form={form} onSubmit={onSubmit} className="flex flex-col gap-6">
                  {error && (
                    <Alert variant="error">
                      <AlertTitle>Couldn't reset password</AlertTitle>
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}

                  <FormField
                    control={form.control}
                    name="password"
                    rules={{
                      required: "Password is required",
                      minLength: { value: 8, message: "At least 8 characters" },
                    }}
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormControl>
                          <Input
                            passwordToggle
                            label="New password"
                            variant={fieldState.error ? "error" : "default"}
                            {...field}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="confirmPassword"
                    rules={{ required: "Please confirm your password" }}
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormControl>
                          <Input
                            passwordToggle
                            label="Confirm password"
                            variant={fieldState.error ? "error" : "default"}
                            {...field}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <Button
                    type="submit"
                    variant="primary"
                    fullWidth
                    loading={form.formState.isSubmitting}
                  >
                    Reset password
                  </Button>
                </Form>
              </div>

              <p className="mt-auto pt-10 text-[14px] text-[#A9A9A9]">
                <a href="/" className="text-[#171717] dark:text-white underline underline-offset-2">
                  ← Back to sign in
                </a>
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-10">
              <a href="/"><Logo variant="auto" size="lg" /></a>
              <div className="flex flex-col gap-6">
                <CheckCircle size={48} color="#171717" weight="LineDuotone" />
                <div className="flex flex-col gap-2">
                  <h1 className="text-[29px] leading-[1.4] font-light text-[#171717] dark:text-white">
                    Password updated
                  </h1>
                  <p className="text-[18px] leading-[1.4] font-light text-[#A9A9A9]">
                    Your password has been reset.{" "}
                    <a href="/" className="text-[#171717] dark:text-white underline underline-offset-2">
                      Sign in now →
                    </a>
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <RightPanel />
    </div>
  );
}
